import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight, Shield, Truck, CreditCard, Clock, CheckCircle, Package } from "lucide-react";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { useCart, useAuth } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const MA_TAX_RATE = 0.0625; // 6.25%

const CartPage = () => {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeFromCart, clearCart, loading } = useCart();
  const { isAuthenticated, token } = useAuth();
  const [shippingMethod, setShippingMethod] = useState("flat_rate");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [shippingRates, setShippingRates] = useState([]);
  const [loadingRates, setLoadingRates] = useState(false);

  // Safe cart values with defaults
  const safeCart = {
    items: cart?.items || [],
    subtotal: cart?.subtotal || 0,
    discount: cart?.discount || 0,
    total: cart?.total || 0
  };

  // Fetch shipping rates
  useEffect(() => {
    const fetchShippingRates = async () => {
      if (safeCart.total <= 0) return;
      
      setLoadingRates(true);
      try {
        const response = await axios.get(`${API}/shipping/rates`, {
          params: { subtotal: safeCart.total }
        });
        setShippingRates(response.data);
      } catch (error) {
        console.error("Error fetching shipping rates:", error);
        // Fallback rates
        setShippingRates([
          { method: "flat_rate", name: "Flat Rate Shipping", carrier: "Multiple", price: safeCart.total >= 100 ? 0 : 9.99, estimated_days: "5-7 business days" },
          { method: "expedited", name: "Expedited Shipping", carrier: "UPS/FedEx", price: 19.99, estimated_days: "1-2 business days" }
        ]);
      } finally {
        setLoadingRates(false);
      }
    };
    fetchShippingRates();
  }, [safeCart.total]);

  // Get shipping cost based on selected method
  const getShippingCost = () => {
    const rate = shippingRates.find(r => r.method === shippingMethod);
    if (rate) return rate.price;
    // Fallback: Flat rate is $9.99 or FREE over $100, expedited is $19.99
    if (shippingMethod.includes("express")) return 19.99;
    return safeCart.total >= 100 ? 0 : 9.99;
  };

  // Calculate tax (MA 6.25%)
  const taxAmount = safeCart.total * MA_TAX_RATE;
  const shippingCost = getShippingCost();
  const grandTotal = safeCart.total + taxAmount + shippingCost;

  // Group rates by type
  const standardRates = shippingRates.filter(r => !r.method.includes("express"));
  const expeditedRates = shippingRates.filter(r => r.method.includes("express"));

  const handleCheckout = async () => {
    if (!agreedToTerms) {
      toast.error("Please agree to the Terms of Service to continue");
      return;
    }

    // If not authenticated, save cart to localStorage for guest checkout
    if (!isAuthenticated) {
      const guestCartData = {
        items: safeCart.items.map(item => ({
          product_id: item.product_id,
          name: item.name,
          price: item.price * (1 - (item.discount_percent || 0) / 100),
          quantity: item.quantity,
          image: item.image
        })),
        total: safeCart.total
      };
      localStorage.setItem("guestCart", JSON.stringify(guestCartData));
    }

    // Navigate to checkout page (works for both auth and guest)
    navigate("/checkout");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 animate-fade-in" data-testid="cart-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 py-12">
        <div className="container mx-auto px-4">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 uppercase tracking-tight" data-testid="cart-title">
            Shopping Cart
          </h1>
          <p className="text-gray-600 mt-2">
            {safeCart.items.length} {safeCart.items.length === 1 ? 'item' : 'items'} in your cart
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {safeCart.items.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-lg border border-gray-200" data-testid="empty-cart">
            <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl text-gray-900 mb-2">Your cart is empty</h2>
            <p className="text-gray-500 mb-8">Looks like you haven't added any items yet.</p>
            <Link to="/shop">
              <Button className="btn-primary">
                Start Shopping
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
              {safeCart.items.map((item) => {
                const discountedPrice = item.discount_percent > 0
                  ? item.price * (1 - item.discount_percent / 100)
                  : item.price;
                const itemTotal = discountedPrice * item.quantity;

                return (
                  <div
                    key={item.product_id}
                    className="bg-white border border-gray-200 rounded-lg p-4 md:p-6 flex flex-col md:flex-row gap-4"
                    data-testid={`cart-item-${item.product_id}`}
                  >
                    {/* Image */}
                    <Link to={`/product/${item.product_id}`} className="w-full md:w-32 aspect-square flex-shrink-0">
                      <img
                        src={item.image || "https://via.placeholder.com/128x128?text=No+Image"}
                        alt={item.name}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    </Link>

                    {/* Details */}
                    <div className="flex-1 flex flex-col md:flex-row md:items-center gap-4">
                      <div className="flex-1">
                        <Link to={`/product/${item.product_id}`}>
                          <h3 className="text-gray-900 font-medium hover:text-primary transition-colors" data-testid="item-name">
                            {item.name}
                          </h3>
                        </Link>
                        <div className="flex items-center gap-2 mt-1">
                          {item.discount_percent > 0 && (
                            <span className="text-gray-400 line-through text-sm">${item.price.toFixed(2)}</span>
                          )}
                          <span className="text-primary font-medium">${discountedPrice.toFixed(2)}</span>
                        </div>
                      </div>

                      {/* Quantity */}
                      <div className="quantity-selector">
                        <button
                          onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                          className="quantity-btn"
                          data-testid={`decrease-${item.product_id}`}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-14 h-10 flex items-center justify-center text-gray-900 border-x border-gray-200" data-testid="item-quantity">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                          className="quantity-btn"
                          data-testid={`increase-${item.product_id}`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Total */}
                      <div className="text-right min-w-[80px]">
                        <span className="text-gray-900 font-bold text-lg" data-testid="item-total">
                          ${itemTotal.toFixed(2)}
                        </span>
                      </div>

                      {/* Remove */}
                      <button
                        onClick={() => removeFromCart(item.product_id)}
                        className="text-gray-400 hover:text-secondary transition-colors"
                        data-testid={`remove-${item.product_id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Clear Cart */}
              <div className="text-right pt-4">
                <Button
                  variant="outline"
                  onClick={clearCart}
                  className="border-gray-300 text-gray-600 hover:text-gray-900"
                  data-testid="clear-cart-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear Cart
                </Button>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:sticky lg:top-32 lg:self-start space-y-4">
              {/* Shipping Options with Carrier Rates */}
              <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="shipping-options">
                <h3 className="font-heading text-lg font-bold text-gray-900 uppercase mb-4">Shipping Method</h3>
                
                {loadingRates ? (
                  <div className="py-4 text-center text-gray-500">Calculating shipping rates...</div>
                ) : (
                  <div className="space-y-4">
                    {/* Standard Shipping Options */}
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                        <Truck className="w-4 h-4" />
                        Standard Delivery
                        {safeCart.total >= 100 && <span className="text-green-600 text-xs font-bold">(FREE!)</span>}
                      </p>
                      <div className="space-y-2">
                        {standardRates.map((rate) => (
                          <label 
                            key={rate.method}
                            className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                              shippingMethod === rate.method ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <input
                              type="radio"
                              name="shipping"
                              value={rate.method}
                              checked={shippingMethod === rate.method}
                              onChange={(e) => setShippingMethod(e.target.value)}
                              className="mt-0.5"
                            />
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <span className="text-gray-900 text-sm font-medium">{rate.name}</span>
                                <span className={`font-bold text-sm ${rate.price === 0 ? 'text-green-600' : 'text-gray-900'}`}>
                                  {rate.price === 0 ? 'FREE' : `$${rate.price.toFixed(2)}`}
                                </span>
                              </div>
                              <div className="text-gray-500 text-xs mt-0.5">
                                {rate.carrier} • {rate.estimated_days}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                      {safeCart.total < 100 && (
                        <p className="text-xs text-green-600 mt-2 font-medium flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Add ${(100 - safeCart.total).toFixed(2)} more for FREE shipping!
                        </p>
                      )}
                    </div>

                    {/* Expedited Shipping Options */}
                    {expeditedRates.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Express Delivery
                        </p>
                        <div className="space-y-2">
                          {expeditedRates.map((rate) => (
                            <label 
                              key={rate.method}
                              className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                                shippingMethod === rate.method ? 'border-secondary bg-secondary/5' : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <input
                                type="radio"
                                name="shipping"
                                value={rate.method}
                                checked={shippingMethod === rate.method}
                                onChange={(e) => setShippingMethod(e.target.value)}
                                className="mt-0.5"
                              />
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-900 text-sm font-medium">{rate.name}</span>
                                  <span className="text-secondary font-bold text-sm">${rate.price.toFixed(2)}</span>
                                </div>
                                <div className="text-gray-500 text-xs mt-0.5">
                                  {rate.carrier} • {rate.estimated_days}
                                </div>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Carrier Info Note */}
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                      <div className="flex items-start gap-2 text-xs text-blue-700">
                        <Package className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>
                          <strong>Note:</strong> Exact shipping rates from UPS will be calculated at checkout based on your delivery address.
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Order Summary */}
              <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="order-summary">
                <h2 className="font-heading text-xl font-bold text-gray-900 uppercase mb-6">Order Summary</h2>

                <div className="space-y-3 pb-6 border-b border-gray-200">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal</span>
                    <span data-testid="subtotal">${safeCart.subtotal.toFixed(2)}</span>
                  </div>
                  {safeCart.discount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount</span>
                      <span data-testid="discount">-${safeCart.discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-gray-600">
                    <span>Shipping</span>
                    <span className={shippingCost === 0 ? "text-green-600 font-medium" : ""}>
                      {shippingCost === 0 ? 'FREE' : `$${shippingCost.toFixed(2)}`}
                    </span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Tax (MA 6.25%)</span>
                    <span data-testid="tax">${taxAmount.toFixed(2)}</span>
                  </div>
                </div>

                <div className="flex justify-between text-gray-900 font-bold text-xl mt-6 mb-6">
                  <span>Total</span>
                  <span data-testid="total">${grandTotal.toFixed(2)}</span>
                </div>

                {/* Terms Agreement */}
                <div className="flex items-start gap-3 mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <Checkbox 
                    id="terms" 
                    checked={agreedToTerms}
                    onCheckedChange={setAgreedToTerms}
                    data-testid="terms-checkbox"
                  />
                  <label htmlFor="terms" className="text-sm text-gray-600 cursor-pointer">
                    I agree to the{' '}
                    <Link to="/terms" className="text-primary hover:underline" target="_blank">
                      Terms of Service
                    </Link>
                    ,{' '}
                    <Link to="/returns" className="text-primary hover:underline" target="_blank">
                      Returns Policy
                    </Link>
                    , and{' '}
                    <Link to="/privacy" className="text-primary hover:underline" target="_blank">
                      Privacy Policy
                    </Link>
                  </label>
                </div>

                <Button
                  onClick={handleCheckout}
                  disabled={!agreedToTerms}
                  className="w-full bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-wider py-6 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="checkout-btn"
                >
                  Proceed to Checkout
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>

                {/* Trust Badges */}
                <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
                  <div className="trust-badge">
                    <Shield className="w-5 h-5" />
                    <span>Secure SSL Checkout</span>
                  </div>
                  <div className="trust-badge">
                    <CheckCircle className="w-5 h-5" />
                    <span>PCI DSS Compliant</span>
                  </div>
                  <div className="trust-badge">
                    <CreditCard className="w-5 h-5" />
                    <span>NMI Secure Payments</span>
                  </div>
                </div>
              </div>

              {/* Continue Shopping */}
              <Link to="/shop" className="block">
                <Button variant="outline" className="w-full border-gray-300 text-gray-600 hover:text-gray-900">
                  Continue Shopping
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartPage;
